import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/axios';
import { STORAGE_KEYS } from '../../constants';
import './NearbyBinsMap.css';

const DEFAULT_CENTER = { lat: 28.6139, lng: 77.2090 };

const MAX_DISTANCE_METERS = 5000; // 5 km

const NearbyBinsMap = () => {
  const { user } = useAuth();
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const infoWindowRef = useRef(null);
  const markersRef = useRef([]);
  const pollingRef = useRef(null);

  const [mapsReady, setMapsReady] = useState(Boolean(window.google?.maps));
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyBins, setNearbyBins] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [binsLoading, setBinsLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [sensorOnline, setSensorOnline] = useState(null);

  const calculateDistanceKm = useCallback((from, to) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth radius in km
    const dLat = toRad(to.lat - from.lat);
    const dLng = toRad(to.lng - from.lng);
    const lat1 = toRad(from.lat);
    const lat2 = toRad(to.lat);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Number((R * c).toFixed(2));
  }, []);

  useEffect(() => {
    if (mapsReady) {
      return;
    }

    const interval = setInterval(() => {
      if (window.google?.maps) {
        setMapsReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [mapsReady]);

  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current) {
      return;
    }

    mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 14,
      mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true
    });

    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, [mapsReady]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
  }, []);

  const addMarkers = useCallback(
    (bins, userPos) => {
      if (!mapInstanceRef.current || !window.google?.maps) {
        return;
      }

      clearMarkers();

      if (userPos) {
        const userMarker = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position: userPos,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#1d4ed8',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          },
          title: 'Your Location'
        });

        markersRef.current.push(userMarker);
        mapInstanceRef.current.setCenter(userPos);
      }

      bins.forEach((bin) => {
        const marker = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position: bin.position,
          icon: {
            path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 5,
            fillColor: bin.status === 'full' ? '#dc2626' : bin.status === 'maintenance' ? '#f97316' : '#22c55e',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 1
          },
          title: `${bin.binId} (${bin.status})`
        });

        marker.addListener('click', () => {
          if (!infoWindowRef.current) {
            return;
          }

          infoWindowRef.current.setContent(
            `<div style="padding:8px 12px; max-width:220px;">
              <strong>${bin.binId}</strong><br/>
              <span>${bin.address}</span><br/>
              <span>Status: ${bin.status}</span><br/>
              <span>Fill Level: ${bin.capacity ?? 'N/A'}%</span><br/>
              <span>Distance: ${bin.distanceKm} km</span>
            </div>`
          );
          infoWindowRef.current.open({ anchor: marker, map: mapInstanceRef.current });
        });

        markersRef.current.push(marker);
      });
    },
    [clearMarkers]
  );

  const fetchNearbyBins = useCallback(
    async (coords) => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

      if (!token) {
        toast.error('Please log in to view nearby bins.');
        setNearbyBins([]);
        setSensorOnline(null);
        setInitialLoading(false);
        return;
      }

      setBinsLoading(true);

      try {
        const response = await api.get('/bins/nearby', {
          params: {
            latitude: coords.lat,
            longitude: coords.lng,
            maxDistance: MAX_DISTANCE_METERS
          }
        });

        const bins = Array.isArray(response.data) ? response.data : [];

        setSensorOnline(bins.some((bin) => bin?.sensorData?.hasSensor));

        if (!bins.length) {
          setNearbyBins([]);
          toast.error('No nearby bins found within 5km radius.');
          setSensorOnline(false);
          setInitialLoading(false);
          return;
        }

        const transformed = bins
          .map((bin) => {
            const [lng, lat] = bin?.location?.coordinates || [];

            if (typeof lat !== 'number' || typeof lng !== 'number') {
              return null;
            }

            const types = Array.isArray(bin.type) ? bin.type : [bin.type || 'General'];
            const sensorData = bin.sensorData || {};
            const capacity = Math.max(0, Math.min(100, Math.round(sensorData.level ?? bin.currentCapacity ?? 0)));
            const status = (sensorData.status || bin.status || 'unknown').toLowerCase();

            return {
              id: bin._id || bin.binId,
              binId: bin.binId || bin._id || 'Unknown Bin',
              position: { lat, lng },
              address:
                typeof bin.address === 'string'
                  ? bin.address
                  : [
                      bin.address?.streetAddress,
                      bin.address?.locality,
                      bin.address?.city
                    ]
                      .filter(Boolean)
                      .join(', ') || 'Address not available',
              types,
              status,
              capacity,
              hasSensor: Boolean(sensorData.hasSensor),
              distanceKm: calculateDistanceKm(coords, { lat, lng }),
              lastUpdate: sensorData.lastSensorUpdate || bin.lastPickup || null
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.distanceKm - b.distanceKm);

        if (!transformed.length) {
          setNearbyBins([]);
          toast.error('No nearby bins found within 5km radius.');
          setInitialLoading(false);
          return;
        }

        setNearbyBins(transformed);
        addMarkers(transformed, coords);
      } catch (error) {
        console.error('Failed to fetch nearby bins:', error);
        toast.error('Failed to load nearby bins. Please try again.');
        setNearbyBins([]);
        setSensorOnline(false);
      } finally {
        setBinsLoading(false);
        setInitialLoading(false);
      }
    },
    [addMarkers, calculateDistanceKm]
  );

  const acquireLocation = useCallback(() => {
    setInitialLoading(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported. Showing default area.');
      setUserLocation(DEFAULT_CENTER);
      fetchNearbyBins(DEFAULT_CENTER);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(coords);
        fetchNearbyBins(coords);
      },
      (err) => {
        console.warn('Geolocation error:', err);
        setLocationError('Location access denied. Showing bins near the default city centre.');
        setUserLocation(DEFAULT_CENTER);
        fetchNearbyBins(DEFAULT_CENTER);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, [fetchNearbyBins]);

  useEffect(() => {
    if (!mapsReady) {
      return;
    }
    acquireLocation();
  }, [mapsReady, acquireLocation, user?.id]);

  useEffect(() => {
    if (!userLocation) {
      return;
    }

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(() => {
      fetchNearbyBins(userLocation);
    }, 30000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [userLocation, fetchNearbyBins]);

  useEffect(() => {
    if (!mapInstanceRef.current || !userLocation) {
      return;
    }
    mapInstanceRef.current.setCenter(userLocation);
  }, [userLocation]);

  useEffect(
    () => () => {
      clearMarkers();
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    },
    [clearMarkers]
  );

  const handleRefresh = () => {
    acquireLocation();
  };

  const statusBadge = (bin) => {
    const status = bin.status;

    if (status === 'full') {
      return { label: 'Full', className: 'status-full', emoji: '🔴' };
    }
    if (status === 'maintenance') {
      return { label: 'Maintenance', className: 'status-maintenance', emoji: '🛠️' };
    }
    if (status === 'partial') {
      return { label: 'Partial', className: 'status-partial', emoji: '🟡' };
    }

    return { label: 'Available', className: 'status-available', emoji: '🟢' };
  };

  if (!mapsReady) {
    return (
      <div className="map-loading">
        <Loader className="animate-spin" size={32} />
        <p>Loading map resources...</p>
      </div>
    );
  }

  return (
    <div className="nearby-bins-map">
      <div className="map-header">
        <h2>
          <MapPin size={24} /> Nearby Bins
        </h2>
        <div className="map-header-actions">
          <div
            className={`sensor-pill ${sensorOnline === null ? 'sensor-unknown' : sensorOnline ? 'sensor-online' : 'sensor-offline'}`}
          >
            <span role="img" aria-label="sensor">
              📡
            </span>
            {sensorOnline === null ? 'Sensor status unknown' : sensorOnline ? 'Sensors online' : 'Sensors offline'}
          </div>
          <button type="button" className="refresh-button" onClick={handleRefresh} disabled={binsLoading}>
            <RefreshCw size={16} className={binsLoading ? 'animate-spin' : ''} />
            {binsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {locationError && (
        <div className="map-warning">
          <AlertCircle size={16} />
          <span>{locationError}</span>
        </div>
      )}

      <div className="map-container">
        <div ref={mapContainerRef} className="map" />
        {(initialLoading || binsLoading) && (
          <div className="map-loading-overlay">
            <Loader className="animate-spin" size={28} />
            <span>{initialLoading ? 'Locating you...' : 'Loading nearby bins...'}</span>
          </div>
        )}
      </div>

      <div className="bins-list">
        <div className="bins-list-header">
          <h3>
            Nearby Bins ({nearbyBins.length})
          </h3>
          <p className="location-info">Showing results within a 5 km radius</p>
        </div>

        {nearbyBins.length === 0 ? (
          <div className="no-bins">
            <AlertCircle size={48} className="no-bins-icon" />
            <h4>No bins to display</h4>
            <p>Try refreshing the location or check back later.</p>
          </div>
        ) : (
          <div className="bins-grid">
            {nearbyBins.map((bin) => {
              const badge = statusBadge(bin);

              return (
                <div key={bin.id} className="bin-card">
                  <div className="bin-card-header">
                    <div className="bin-title">
                      <span className="bin-id">{bin.binId}</span>
                      {bin.hasSensor && <span className="live-indicator">📡 Live</span>}
                    </div>
                    <span className={`status-badge ${badge.className}`}>
                      {badge.emoji} {badge.label}
                    </span>
                  </div>

                  <div className="bin-details">
                    <div className="detail-row">
                      <span className="detail-label">Distance</span>
                      <span className="detail-value">{bin.distanceKm} km</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Fill level</span>
                      <span className="detail-value">{bin.capacity}%</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Type</span>
                      <span className="detail-value">{bin.types.join(', ')}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Address</span>
                      <span className="detail-value">{bin.address}</span>
                    </div>
                    {bin.lastUpdate && (
                      <div className="detail-row">
                        <span className="detail-label">Last update</span>
                        <span className="detail-value">
                          {new Date(bin.lastUpdate).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="get-directions-btn"
                    onClick={() => {
                      const destination = `${bin.position.lat},${bin.position.lng}`;
                      const origin = userLocation ? `${userLocation.lat},${userLocation.lng}` : '';
                      const url = origin
                        ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`
                        : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
                      window.open(url, '_blank');
                    }}
                  >
                    <Navigation size={18} />
                    Directions
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NearbyBinsMap;
